/**
 * Runtime contract for the operational archetype's previewable paid boundaries
 * (capability gap G5).
 *
 * Split out of `FmOperationalWorkspaceRoot.validation.ts` so neither file
 * crosses the 500-line gate.
 */
import { assertFmUnlockPreviewDescriptor } from './FmUnlockPreview.validation';
import { requireResolvingControl } from './FmOperationalWorkspaceState.validation';
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalOneOf as oneOf,
  operationalText as text,
} from './FmOperationalWorkspaceValidation.shared';

/**
 * A paid boundary is a PREVIEW, so it must be able to sell what it withholds:
 * the unlock control has to exist, has to be usable, and has to stay usable while
 * whatever inhibits the rest of the workspace holds. A boundary whose CTA is
 * missing or greyed is the disabled blank in a nicer frame
 * (operations-planning.md acceptance criterion 1).
 */
export function validatePaidBoundaries(
  value: unknown,
  controls: Record<string, unknown>[],
  stageIds: ReadonlySet<string>,
  regionIds: ReadonlySet<string>,
): void {
  const boundaries = array(value, 'descriptor.paidBoundaries');
  const ids = new Set<string>();
  boundaries.forEach((item, index) => {
    const path = `descriptor.paidBoundaries[${index}]`;
    const boundary = object(item, path);
    exact(boundary, new Set(['id', 'scope', 'preview']), path);
    ids.add(text(boundary.id, `${path}.id`));
    const scope = object(boundary.scope, `${path}.scope`);
    const kind = oneOf(scope.kind, ['workspace', 'stage', 'region'], `${path}.scope.kind`);
    if (kind === 'stage') {
      exact(scope, new Set(['kind', 'stageId']), `${path}.scope`);
      if (!stageIds.has(text(scope.stageId, `${path}.scope.stageId`))) {
        fail(`${path}.scope.stageId`, 'must reference a declared stage');
      }
    } else if (kind === 'region') {
      exact(scope, new Set(['kind', 'regionId']), `${path}.scope`);
      if (!regionIds.has(text(scope.regionId, `${path}.scope.regionId`))) {
        fail(`${path}.scope.regionId`, 'must reference a declared region');
      }
    } else {
      exact(scope, new Set(['kind']), `${path}.scope`);
    }
    const preview = object(boundary.preview, `${path}.preview`);
    assertFmUnlockPreviewDescriptor(preview, `${path}.preview`, fail);
    /* The CTA resolves the boundary that produced it, so it takes the same
       contract every state-surface control takes: declared, enabled, and usable
       while the workspace is inhibited. */
    requireResolvingControl(preview.unlockActionId, `${path}.preview.unlockActionId`, controls);
  });
  if (ids.size !== boundaries.length) fail('descriptor.paidBoundaries', 'must use unique ids');
}
